import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { toast } from "@/lib/notify";
import API from "@/lib/api";
import { Plus, Network, MoreHorizontal, Pencil, Trash2, RefreshCw, Search, Check, ChevronsUpDown } from "lucide-react";
import { format } from "date-fns";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Pagination, paginate } from "@/components/Pagination";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import CountryCombobox from "@/components/CountryCombobox";
import { useSortableTable } from "@/hooks/useSortableTable";
import { SortableHead } from "@/components/SortableHead";
import { useIdempotentSubmit } from "@/hooks/useIdempotentSubmit";

const SYNC_STYLES = {
  synced: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  pending: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  retrying: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  failed: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
  skipped: "bg-slate-500/10 text-slate-500 dark:text-slate-400 border-slate-500/20",
};

// Normalize any raw org-sync status into three display labels.
const orgSyncDisplay = (status) => {
  const raw = (status || "").toLowerCase();
  if (raw === "synced" || raw === "success") return { label: "Synced", className: SYNC_STYLES.synced };
  if (raw === "failed" || raw === "error") return { label: "Failed", className: SYNC_STYLES.failed };
  return { label: "No Access", className: SYNC_STYLES.skipped };
};

const EMPTY_FORM = { name: "", country: "", timezone: "", jurisdiction: "" };
const EMPTY_ERRORS = { name: "", country: "", timezone: "" };

const IANA_TIMEZONES = [
  "UTC",
  "Africa/Cairo", "Africa/Casablanca", "Africa/Johannesburg", "Africa/Lagos", "Africa/Nairobi",
  "America/Anchorage", "America/Argentina/Buenos_Aires", "America/Bogota", "America/Caracas",
  "America/Chicago", "America/Denver", "America/Halifax", "America/Lima", "America/Los_Angeles",
  "America/Mexico_City", "America/New_York", "America/Phoenix", "America/Santiago",
  "America/Sao_Paulo", "America/St_Johns", "America/Toronto", "America/Vancouver",
  "Asia/Baghdad", "Asia/Baku", "Asia/Bangkok", "Asia/Beirut", "Asia/Colombo", "Asia/Dhaka",
  "Asia/Dubai", "Asia/Ho_Chi_Minh", "Asia/Hong_Kong", "Asia/Jakarta", "Asia/Jerusalem",
  "Asia/Kabul", "Asia/Karachi", "Asia/Kathmandu", "Asia/Kolkata", "Asia/Kuala_Lumpur",
  "Asia/Kuwait", "Asia/Manila", "Asia/Muscat", "Asia/Qatar", "Asia/Riyadh", "Asia/Seoul",
  "Asia/Shanghai", "Asia/Singapore", "Asia/Taipei", "Asia/Tashkent", "Asia/Tehran",
  "Asia/Tokyo", "Asia/Ulaanbaatar", "Asia/Yangon", "Asia/Yerevan",
  "Atlantic/Azores", "Atlantic/Bermuda", "Atlantic/Cape_Verde", "Atlantic/Reykjavik",
  "Australia/Adelaide", "Australia/Brisbane", "Australia/Darwin", "Australia/Melbourne",
  "Australia/Perth", "Australia/Sydney",
  "Europe/Amsterdam", "Europe/Athens", "Europe/Belgrade", "Europe/Berlin", "Europe/Brussels",
  "Europe/Bucharest", "Europe/Budapest", "Europe/Copenhagen", "Europe/Dublin", "Europe/Helsinki",
  "Europe/Istanbul", "Europe/Kiev", "Europe/Lisbon", "Europe/London", "Europe/Madrid",
  "Europe/Moscow", "Europe/Oslo", "Europe/Paris", "Europe/Prague", "Europe/Rome",
  "Europe/Sofia", "Europe/Stockholm", "Europe/Vienna", "Europe/Warsaw", "Europe/Zurich",
  "Indian/Maldives", "Indian/Mauritius",
  "Pacific/Auckland", "Pacific/Fiji", "Pacific/Guam", "Pacific/Honolulu", "Pacific/Midway",
  "Pacific/Wellington",
];

function TimezoneCombobox({ value, onChange, testId, invalid = false }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between bg-secondary/50 font-normal ${invalid ? "border-red-500/50" : ""}`}
          data-testid={testId}
        >
          <span className={value ? "" : "text-muted-foreground"}>{value || "Select timezone"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search timezone..." data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              {IANA_TIMEZONES.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  onSelect={() => { onChange(tz); setOpen(false); }}
                  data-testid={`${testId}-option-${tz}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === tz ? "opacity-100" : "opacity-0"}`} />
                  {tz}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function OrganizationsPage() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const { submitting, run: runSubmit } = useIdempotentSubmit();

  const fetchOrgs = useCallback(async () => {
    try {
      const { data } = await API.get("/organizations");
      setOrgs(data);
    } catch {
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const filtered = orgs.filter((o) => {
    // Trim leading/trailing whitespace before matching.
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return o.name?.toLowerCase().includes(q) || o.country?.toLowerCase().includes(q);
  });
  const { sorted: sortedOrgs, sortKey, sortDir, toggleSort } = useSortableTable(filtered);
  const { paginatedItems: pagedOrgs, totalPages: orgsTotalPages, safePage: orgsSafePage } = paginate(sortedOrgs, page, pageSize);

  const openCreate = () => {
    setEditOrg(null);
    setForm(EMPTY_FORM);
    setErrors(EMPTY_ERRORS);
    setDialogOpen(true);
  };

  const openEdit = (o) => {
    setEditOrg(o);
    setForm({
      name: o.name || "",
      country: o.country || "",
      timezone: o.timezone || "",
      jurisdiction: o.jurisdiction || "",
    });
    setErrors(EMPTY_ERRORS);
    setDialogOpen(true);
  };

  const validate = () => {
    const next = {
      name: form.name.trim() ? "" : "Name is required",
      country: form.country.trim() ? "" : "Country is required",
      timezone: form.timezone.trim() ? "" : "Timezone is required",
    };
    setErrors(next);
    return !next.name && !next.country && !next.timezone;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;                    // hard-block re-entry
    if (!validate()) return;
    await runSubmit(async (idempotencyKey) => {
      try {
        if (editOrg) {
          await API.put(`/organizations/${editOrg.id}`, form);
          toast.success("Organization updated");
        } else {
          await API.post("/organizations", form, {
            headers: { "Idempotency-Key": idempotencyKey },
          });
          toast.success("Organization created — GL, Radar, and Recon sync scheduled.");
        }
        setDialogOpen(false);
        await fetchOrgs();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Operation failed");
      }
    });
  };

  const handleDelete = async (o) => {
    try {
      await API.delete(`/organizations/${o.id}`);
      toast.success("Organization deleted");
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const retrySync = async (o) => {
    try {
      await API.post(`/organizations/${o.id}/sync-gl`);
      toast.success("GL sync queued");
      setTimeout(fetchOrgs, 2000);
    } catch {
      toast.error("Failed to schedule sync");
    }
  };

  const retryRadarSync = async (o) => {
    try {
      await API.post(`/organizations/${o.id}/sync-radar`);
      toast.success("Radar sync queued");
      setTimeout(fetchOrgs, 2000);
    } catch {
      toast.error("Failed to schedule Radar sync");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="organizations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Organizations</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Top-level tenancy boundary. Every fund, investor, and user belongs to an organization.
          </p>
        </div>
        <Button onClick={openCreate} className="shadow-[0_0_20px_rgba(108,71,196,0.2)]" data-testid="create-organization-button">
          <Plus className="w-4 h-4 mr-2" /> Add Organization
        </Button>
      </div>

      <Card data-testid="organizations-table-card">
        <CardHeader className="p-6 pb-4">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-secondary/50 h-10"
              data-testid="organization-search-input"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <SortableHead sortKey="name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold pl-6">Name</SortableHead>
                <SortableHead sortKey="country" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Country</SortableHead>
                <SortableHead sortKey="timezone" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Timezone</SortableHead>
                <SortableHead sortKey="jurisdiction" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Jurisdiction</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">GL Sync</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Radar Sync</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Recon Sync</TableHead>
                <SortableHead sortKey="created_at" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Created</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={9} className="pl-6"><div className="h-10 bg-secondary/30 rounded animate-pulse" /></TableCell>
                  </TableRow>
                ))
              ) : sortedOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-12">
                    {search ? "No organizations match the filter" : "No organizations yet. Create one to get started."}
                  </TableCell>
                </TableRow>
              ) : (
                pagedOrgs.map((o) => (
                  <TableRow key={o.id} className="table-row-interactive" data-testid={`org-row-${o.id}`}>
                    <TableCell className="pl-6 font-medium flex items-center gap-2">
                      <Network className="w-4 h-4 text-primary" />
                      {o.name}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{o.country}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{o.timezone}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{o.jurisdiction || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${orgSyncDisplay(o.gl_sync_status).className}`} data-testid={`org-gl-status-${o.id}`}>
                        {orgSyncDisplay(o.gl_sync_status).label}
                      </Badge>
                      {o.gl_sync_error && o.gl_sync_status !== "synced" && (
                        <p className="text-[10px] text-red-400 mt-1 truncate max-w-[200px]" title={o.gl_sync_error}>{o.gl_sync_error}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${orgSyncDisplay(o.radar_sync_status).className}`} data-testid={`org-radar-status-${o.id}`}>
                        {orgSyncDisplay(o.radar_sync_status).label}
                      </Badge>
                      {o.radar_sync_error && o.radar_sync_status === "failed" && (
                        <p className="text-[10px] text-red-400 mt-1 truncate max-w-[200px]" title={o.radar_sync_error}>{o.radar_sync_error}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider ${orgSyncDisplay(o.recon_sync_status).className}`}
                        title={o.recon_sync_status === "failed" ? (o.recon_sync_error || "") : (o.recon_sync_status === "skipped" ? (o.recon_sync_error || "Not required") : "")}
                        data-testid={`org-recon-status-${o.id}`}
                      >
                        {orgSyncDisplay(o.recon_sync_status).label}
                      </Badge>
                      {o.recon_sync_error && o.recon_sync_status === "failed" && (
                        <p className="text-[10px] text-red-400 mt-1 truncate max-w-[200px]" title={o.recon_sync_error}>{o.recon_sync_error}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {o.created_at ? format(new Date(o.created_at), "MMMM d, yyyy") : "—"}
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="w-8 h-8" data-testid={`org-actions-${o.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-card border-border w-48">
                          <DropdownMenuItem onClick={() => openEdit(o)} data-testid={`org-edit-${o.id}`}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          {(o.gl_sync_status !== "synced") && (
                            <DropdownMenuItem onClick={() => retrySync(o)} data-testid={`org-retry-sync-${o.id}`}>
                              <RefreshCw className="w-4 h-4 mr-2" /> Retry GL sync
                            </DropdownMenuItem>
                          )}
                          {(o.radar_sync_status === "failed" || o.radar_sync_status === "pending") && (
                            <DropdownMenuItem onClick={() => retryRadarSync(o)} data-testid={`org-retry-radar-${o.id}`}>
                              <RefreshCw className="w-4 h-4 mr-2" /> Retry Radar sync
                            </DropdownMenuItem>
                          )}
                          {o.name !== "Default" && (
                            <DropdownMenuItem
                              onClick={() => setConfirmDelete(o)}
                              className="text-red-400 focus:text-red-400"
                              data-testid={`org-delete-${o.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {!loading && sortedOrgs.length > 0 && (
            <div className="px-6 pb-4">
              <Pagination
                currentPage={orgsSafePage}
                totalPages={orgsTotalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border sm:max-w-lg" data-testid="org-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Network className="w-5 h-5 text-primary" />
              {editOrg ? "Edit Organization" : "Create New Organization"}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {editOrg
                ? "Update organization details below."
                : "Organization created — GL, Radar, and Recon sync scheduled."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === "Enter" && submitting) e.preventDefault(); }} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: "" }); }}
                className={`bg-secondary/50 ${errors.name ? "border-red-500/50" : ""}`}
                data-testid="org-name-input"
                placeholder="Acme Capital Partners"
                aria-invalid={!!errors.name}
              />
              {errors.name && <p className="text-xs text-red-400" data-testid="org-name-error">{errors.name}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Country *</Label>
                <CountryCombobox
                  value={form.country}
                  onChange={(v) => { setForm({ ...form, country: v }); if (errors.country) setErrors({ ...errors, country: "" }); }}
                  testId="org-country-input"
                  invalid={!!errors.country}
                />
                {errors.country && <p className="text-xs text-red-400" data-testid="org-country-error">{errors.country}</p>}
              </div>
              <div className="space-y-2">
                <Label>Timezone *</Label>
                <TimezoneCombobox
                  value={form.timezone}
                  onChange={(v) => { setForm({ ...form, timezone: v }); if (errors.timezone) setErrors({ ...errors, timezone: "" }); }}
                  testId="org-timezone-input"
                  invalid={!!errors.timezone}
                />
                {errors.timezone && <p className="text-xs text-red-400" data-testid="org-timezone-error">{errors.timezone}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Jurisdiction</Label>
              <Input
                value={form.jurisdiction}
                onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                className="bg-secondary/50"
                data-testid="org-jurisdiction-input"
                placeholder="Delaware, USA"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="org-submit-button">
                {submitting
                  ? (editOrg ? "Saving…" : "Creating…")
                  : (editOrg ? "Update" : "Create Organization")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}
        title="Delete Organization"
        description={`Are you sure you want to delete "${confirmDelete?.name}"? This action cannot be undone. Organizations with attached users, funds, or investors cannot be deleted.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => { handleDelete(confirmDelete); setConfirmDelete(null); }}
      />
    </div>
  );
}
